// src/lib/map/mapCredits.js
//
// What a map's credits footer shows (MapCreditsFooter.jsx, footprint restyle
// S-e slice 2): the credits of the sources drawn now, worked out as MapLibre's
// own AttributionControl does, so the footer can lay them out itself.
//
// Why not MapLibre's control: it renders every credit as one HTML string, so
// one credit cannot be truncated while the others stay whole. DB's rule
// (2026-11-02): when the text is wider than the room, the Copernicus line
// truncates, with its full text in a tooltip; Mapbox's and OpenStreetMap's
// links and "Improve this map" never do. A map with this footer is built with
// attributionControl: false.
//
//   drawnCredits   the attribution of each source a visible layer draws
//                  (MapLibre's `used`, or used for terrain), blank ones
//                  dropped, and one contained in another dropped: MapLibre's
//                  own rules, in its order (shortest first).
//   parseCredit    a credit's HTML as text and links, read by the browser's
//                  parser; nothing but text, <a href="http(s)…"> and <strong>
//                  survives, so a credit is never injected as HTML.
//   isCopernicus   the credit that may truncate.
//
// The credits stay on the sources (lib/map/attribution.js): Mapbox's on the
// basemap, followMapboxCredit keeping it on the style its tiles now draw;
// Copernicus's on a Sentinel-2 source, so it comes and goes with that source.
import { basemapAttribution, mapboxStyleIdOf } from "./attribution";

/** The tile managers of a map's style: MapLibre 5's tileManagers, or 4's sourceCaches. */
function managersOf(map) {
  const style = map?.style;
  return style?.tileManagers ?? style?.sourceCaches ?? null;
}

/**
 * The credits of the sources a map draws now, in MapLibre's order, each with
 * the Mapbox style its source's tiles draw (null for any other source), for
 * the Improve link.
 * @returns {{html: string, styleId: string|null}[]}
 */
export function drawnCredits(map) {
  const managers = managersOf(map);
  if (!managers) return [];
  const seen = new Map();
  for (const id of Object.keys(managers)) {
    const m = managers[id];
    if (!m || !(m.used || m.usedForTerrain)) continue;
    const src = m.getSource?.();
    const html = src?.attribution;
    if (typeof html !== "string" || !html.trim() || seen.has(html)) continue;
    seen.set(html, mapboxStyleIdOf(src.tiles?.[0]));
  }
  const all = [...seen.keys()].sort((a, b) => a.length - b.length);
  return all
    .filter((c, i) => !all.slice(i + 1).some((longer) => longer.includes(c)))
    .map((html) => ({ html, styleId: seen.get(html) }));
}

/** The credit Copernicus asks for (copernicusAttribution): the one that may truncate. */
export const isCopernicus = (html) => /Copernicus/.test(html ?? "");

const SAFE_HREF = /^https?:\/\//i;

/**
 * A credit's HTML as parts: {text} and {href, text, strong}. Anything else in
 * the HTML is read as its text.
 */
export function parseCredit(html) {
  const doc = new DOMParser().parseFromString(`<body>${html ?? ""}</body>`, "text/html");
  const parts = [];
  const walk = (node, strong) => {
    for (const n of node.childNodes) {
      if (n.nodeType === 3) {
        if (n.textContent) parts.push({ text: n.textContent, strong });
      } else if (n.nodeType === 1) {
        const tag = n.tagName.toLowerCase();
        const href = tag === "a" ? n.getAttribute("href") ?? "" : "";
        if (tag === "a" && SAFE_HREF.test(href)) {
          parts.push({ href, text: n.textContent.trim(), strong: strong || Boolean(n.querySelector("strong, b")) });
        } else {
          walk(n, strong || tag === "strong" || tag === "b");
        }
      }
    }
  };
  walk(doc.body, false);
  return parts;
}

/**
 * Keep a Mapbox raster source's credit on the style its tiles draw now: a
 * basemap switched by setTiles (Explore's picker, purchase mode) names its own
 * style and adds or drops Maxar. Returns the detach. A removed map is never
 * called (DB, 2026-11-02): one already removed is not followed, and after
 * its remove event the detach does nothing (MapLibre dropped the listeners).
 *
 * A map with no style yet is not read (REM-714): MapLibre's getSource is
 * this.style.getSource(id), and a map made before its style (or from another
 * map's getStyle() before that one loaded) has none. The credit is set on its
 * style.load. Not isStyleLoaded(): that is false while any source is loading,
 * which is when the sourcedata this follows arrives.
 */
export function followMapboxCredit(map, sourceId) {
  if (!map || map._removed) return () => {};
  let live = true;
  const recredit = () => {
    if (!live || !map.style) return;
    const src = map.getSource?.(sourceId);
    const id = mapboxStyleIdOf(src?.tiles?.[0]);
    if (src && id && src.attribution !== basemapAttribution(id)) src.attribution = basemapAttribution(id);
  };
  const removed = () => { live = false; };
  map.on("sourcedata", recredit);
  map.on("style.load", recredit);
  map.on("remove", removed);
  recredit();
  return () => {
    if (!live || map._removed) return;
    live = false;
    map.off("sourcedata", recredit);
    map.off("style.load", recredit);
    map.off("remove", removed);
  };
}
